/* @flow */

import * as React from 'react';

import { MediaTypeProvider } from '@sonyged/ext-proc';

import { type FinishedRound, type NewRound, Round } from './domain/Round';
import { PizzaResearchChooseBase } from './PizzaResearchChooseBase';
import { Rng } from '../../../utils/Random';
import { impossible } from './domain/utils';
import AppContext from '../../../AppContext';
import Menu from '../../ice/fields/Menu';
import PizzaBakingView from './PizzaBakingView';
import PizzaBuilderView from './PizzaBuilderView';
import PizzaCookingView from './PizzaCookingView';
import PizzaEatingView from './PizzaEatingView';
import PizzaOrderView from './PizzaOrderView';
import PizzaResearchResultView from './PizzaResearchResultView';
import PizzaResearchTopView from './PizzaResearchTopView';
import PizzaSetChangableView from './PizzaSetChangableView';
import ResearchIntroModal from './ResearchIntroModal';

type Props = {};

type Stage =
  | {
      stage: 'start' | 'repeat' | 'setChangable' | 'order' | 'build',
      round: NewRound,
    }
  | {
      stage: 'cook' | 'bake' | 'eat' | 'result' | 'choose-base',
      round: FinishedRound,
    };

type Past = { round: ?FinishedRound };

type State = {
  backstack: Array<{ current: Stage, past: Past }>,
  past: Past,
  introModal: boolean,
  changable: 'all' | 'topping' | 'crust' | 'sauce',
  current: Stage,
};

class PizzaFieldResearchPage extends React.Component<Props, State> {
  static contextType = AppContext;

  constructor(props: Props) {
    super(props);

    const rng = new Rng(Math.random());

    this.state = {
      backstack: [],
      introModal: true,
      changable: 'all',
      past: { round: null },
      current: {
        stage: 'start',
        round: Round.random('', rng),
      },
    };

    (this: any).randomize = this.randomize.bind(this);
    (this: any).switchToSetChangeable = this.switchToSetChangeable.bind(this);
    (this: any).startRound = this.startRound.bind(this);
    (this: any).switchToPizzaForm = this.switchToPizzaForm.bind(this);
    (this: any).switchToCooking = this.switchToCooking.bind(this);
    (this: any).switchToBaking = this.switchToBaking.bind(this);
    (this: any).switchToEating = this.switchToEating.bind(this);
    (this: any).finishRound = this.finishRound.bind(this);
    (this: any).newRound = this.newRound.bind(this);
    (this: any).goBack = this.goBack.bind(this);
    (this: any).switchToChooseBase = this.switchToChooseBase.bind(this);
  }

  componentDidMount() {
    this.context.soundController.bgmPlay('proc_field03_pizza', true);

    // If browser reload this page or access to this page directly,
    // The parent dom is NOT rendered and the IntroModal try to refer to it and failed.
    // This code assures that the parent dom is rendered once.
    requestAnimationFrame(() => {
      this.forceUpdate();
    });
  }

  randomize() {
    const rng = new Rng(Math.random());
    this.setState({
      current: {
        stage: 'start',
        round: Round.random('', rng),
      },
    });
  }

  switchToSetChangeable() {
    const { current, past } = this.state;
    if (current.stage !== 'repeat') return;
    this.setState({
      backstack: this.state.backstack.concat({ current, past }),
      changable: 'crust',
      current: { round: current.round, stage: 'setChangable' },
    });
  }

  startRound() {
    const { current, past } = this.state;
    if (current.stage === 'start') {
      this.setState({
        backstack: this.state.backstack.concat({ current, past }),
        current: { round: current.round, stage: 'order' },
      });
    }
    if (current.stage === 'setChangable') {
      this.setState({
        backstack: this.state.backstack.concat({ current, past }),
        current: { round: current.round, stage: 'build' },
      });
    }
  }

  switchToPizzaForm(round: NewRound) {
    const { current, past } = this.state;
    this.setState({
      backstack: this.state.backstack.concat({ current, past }),
      current: { round, stage: 'build' },
    });
  }

  switchToCooking(round: FinishedRound) {
    this.setState({ current: { round, stage: 'cook' } });
  }

  switchToBaking(round: FinishedRound) {
    this.setState({ current: { round, stage: 'bake' } });
  }

  switchToEating(round: FinishedRound) {
    this.setState({ current: { round, stage: 'eat' } });
  }

  finishRound(round: FinishedRound) {
    this.setState({
      current: { round, stage: 'result' },
    });
  }

  switchToChooseBase(pastRound: FinishedRound, round: FinishedRound) {
    const { current, past } = this.state;
    this.setState({
      backstack: this.state.backstack.concat({ current, past }),
      past: { round: pastRound },
      current: {
        round,
        stage: 'choose-base',
      },
    });
  }

  goBack() {
    const len = this.state.backstack.length;
    if (len) {
      const last = this.state.backstack[len - 1];
      this.setState({
        past: last.past,
        current: last.current,
        backstack: this.state.backstack.slice(0, len - 1),
      });
    }
  }

  newRound(round: FinishedRound) {
    const { current, past } = this.state;
    this.setState({
      backstack: this.state.backstack.concat({ current, past }),
      past: { round },
      current: {
        stage: 'repeat',
        round: { name: round.name, customers: round.customers },
      },
    });
  }

  currentPage() {
    switch (this.state.current.stage) {
      case 'start':
        return (
          <PizzaResearchTopView randomize={this.randomize} round={this.state.current.round} onNext={this.startRound} />
        );
      case 'order':
        return <PizzaOrderView round={this.state.current.round} onNext={this.switchToPizzaForm} onBack={this.goBack} />;
      case 'build':
        return (
          <PizzaBuilderView
            mode="research"
            round={this.state.current.round}
            pastRound={this.state.past.round}
            onNext={this.switchToCooking}
            changable={this.state.changable}
            onBack={this.goBack}
          />
        );
      case 'cook':
        return <PizzaCookingView round={this.state.current.round} onNext={this.switchToBaking} />;
      case 'bake':
        return <PizzaBakingView round={this.state.current.round} onNext={this.switchToEating} />;
      case 'eat':
        return <PizzaEatingView round={this.state.current.round} onNext={this.finishRound} />;
      case 'result':
        return (
          <PizzaResearchResultView
            onRetry={this.switchToChooseBase}
            round={this.state.current.round}
            onNext={this.newRound}
            pastRound={this.state.past.round}
          />
        );
      case 'choose-base':
        return (
          this.state.past.round && (
            <PizzaResearchChooseBase
              onNext={this.newRound}
              onBack={this.goBack}
              round={this.state.current.round}
              pastRound={this.state.past.round}
            />
          )
        );
      case 'repeat':
        return (
          <PizzaResearchTopView
            round={this.state.current.round}
            onNext={this.switchToSetChangeable}
            onBack={this.goBack}
          />
        );
      case 'setChangable':
        if (!this.state.past.round) return null;
        return (
          <PizzaSetChangableView
            round={this.state.past.round}
            onNext={this.startRound}
            changable={this.state.changable}
            onChange={changable => {
              this.setState({ changable });
            }}
            onBack={this.goBack}
          />
        );
      default:
        return impossible(this.state.current.stage);
    }
  }

  render() {
    if (this.state.introModal) {
      return (
        <div id={'proc-pizza-fields-root-page'} className={'pizza'}>
          <ResearchIntroModal title="しらべる" onNextClick={this._onIntroModalClose.bind(this)} />
        </div>
      );
    }

    const showMenu = ['cook', 'bake'].every(p => p !== this.state.current.stage);
    let menuText = '';
    if (this.state.current.stage === 'result' || this.state.current.stage === 'choose-base') {
      menuText = 'しらべる けっか';
    }

    return (
      <MediaTypeProvider>
        <div id={'proc-pizza-fields-root-page'} className="pizza proc-pizza-field-research">
          {showMenu && (
            <Menu
              exitLink={'/pizza/fields/'}
              query={'?skip_intro=true'}
              menuText={menuText}
              title={'おすすめの ピザを つくろう'}
              subTitle={'しらべる'}
              rootNodeId="proc-pizza-fields-root-page"
            />
          )}
          {this.currentPage()}
        </div>
      </MediaTypeProvider>
    );
  }

  _onIntroModalClose() {
    this.setState({ introModal: false });
  }
}

export default PizzaFieldResearchPage;
